import { Link } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Status, WalletType } from "src/types/commonTypes";
import { Wallet } from "src/page/wallets/walletSlice";
import { EMPTY_STRING, walletTypeMapping } from "src/components/constant/constant";
import { bgColorbyName, typeColors } from "src/variable/randomColor";
import HighLighter from "src/components/UI/highlighter/HighLighter";
import { ShortIdChip } from "src/components/UI/shortIdChip";
import StatusPill from "src/components/userComponent/profileHero/StatusPill";
import { BalanceCell, DateStack, EmptyCell, cellClasses } from "src/components/UI/transactionCells";
import { balanceTone } from "src/utils";
import styles from "../internalWalletListCard.module.scss";

const internalWalletColumns = (searchText: string = EMPTY_STRING) => [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
    render: (id: string | number) => (
      <ShortIdChip id={String(id ?? EMPTY_STRING)} copiedLabel="Card ID Copied!" fallback="---" />
    )
  },
  {
    title: "Display ID",
    dataIndex: "displayId",
    key: "displayId",
    render: (displayId: string) => (
      <ShortIdChip id={displayId ?? EMPTY_STRING} copiedLabel="Display ID Copied!" fallback="---" />
    )
  },
  {
    title: "Card",
    dataIndex: "name",
    key: "name",
    render: (name: string, record: Wallet) => {
      if (!name && !record?.id) return <EmptyCell />;

      return (
        <div className={cellClasses.tableWallet}>
          <Link
            to={`/wallets/${record?.id}`}
            className={cellClasses.tableWalletName}
            aria-label={`View details for card ${name}`}
            title={name}
          >
            <HighLighter searchText={searchText} text={name ?? EMPTY_STRING} />
          </Link>
          {record?.description && (
            <div className={cellClasses.tableWalletDesc} title={record.description}>
              {record.description}
            </div>
          )}
        </div>
      );
    }
  },
  {
    title: "Owner",
    dataIndex: "participant",
    key: "participant",
    render: (_value: unknown, record: Wallet) => {
      const participant = record?.participant;
      const group = record?.group;

      const fullName = participant?.fullName || group?.name;

      if (!fullName) return <EmptyCell />;

      const subtitle = participant?.email ?? group?.group_type ?? EMPTY_STRING;
      const linkTo = ((): string | null => {
        if (participant?.id) return `/users/${participant.id}`;
        if (group?.group_id) return `/groups/${group.group_id}`;

        return null;
      })();

      const inner = (
        <>
          <Avatar aria-label="Owner" className={cellClasses.memberAvatar} sx={{ bgcolor: bgColorbyName(fullName) }}>
            {fullName.charAt(0).toLocaleUpperCase()}
          </Avatar>
          <div className={cellClasses.tableMemberDetail}>
            <div className={cellClasses.tableMemberName}>{fullName}</div>
            {subtitle && <div className={cellClasses.tableMemberEmail}>{subtitle}</div>}
          </div>
        </>
      );

      return linkTo ? (
        <Link to={linkTo} className={cellClasses.tableMember}>
          {inner}
        </Link>
      ) : (
        <div className={cellClasses.tableMember}>{inner}</div>
      );
    }
  },
  {
    title: "Balance",
    dataIndex: "balance",
    key: "balance",
    render: (balance: number) => <BalanceCell value={balance} color={balanceTone(balance)} />
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status: Status) => (
      <StatusPill
        variant={status === "ACTIVE" ? "active" : "inactive"}
        label={status === "ACTIVE" ? "Active" : "Archived"}
      />
    )
  },
  {
    title: "Type",
    dataIndex: "walletType",
    key: "walletType",
    render: (walletType: WalletType) => {
      if (!walletType) return <EmptyCell />;

      const tone = typeColors[walletType] ?? "#475569";

      return (
        <span
          className={styles.typePill}
          style={{
            backgroundColor: `${tone}14`,
            borderColor: `${tone}40`,
            color: tone
          }}
        >
          {walletTypeMapping[walletType] ?? walletType}
        </span>
      );
    }
  },
  {
    title: "Account",
    dataIndex: "organisationId",
    key: "organisationId",
    render: (organisationId: string) =>
      organisationId ? (
        <Link to={`/account/${organisationId}`} className={cellClasses.tableWalletName}>
          <ShortIdChip id={organisationId} withCopy={false} withTooltip variant="ghost" />
        </Link>
      ) : (
        <EmptyCell />
      )
  },
  {
    title: "Created At",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (createdAt: string) => <DateStack value={createdAt} />
  },
  {
    title: EMPTY_STRING,
    dataIndex: EMPTY_STRING,
    key: "edit",
    render: (_text: string, record: Wallet) => (
      <Tooltip title="View card" placement="top" arrow>
        <IconButton
          component={Link}
          to={`/wallets/${record?.id}`}
          size="small"
          className={styles.viewBtn}
          aria-label={`View card ${record?.name}`}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
    width: 64
  }
];

export default internalWalletColumns;
